import { z } from 'zod';

/**
 * `GET /health` response contract.
 *
 * `serverTime` is the authoritative clock. The client displays it but never
 * uses its own clock for anything the economy depends on - see
 * `docs/SECURITY.md`.
 */
export const healthResponseSchema = z.object({
  status: z.literal('ok'),
  service: z.string().min(1),
  version: z.string().min(1),
  environment: z.enum(['development', 'test', 'staging', 'production']),
  /** ISO-8601 UTC, produced by the server. */
  serverTime: z.iso.datetime(),
  /**
   * How this process is serving right now, and nothing more.
   *
   * `serving` is the ordinary answer. `maintenance` means the doors are shut
   * for a stage transition. `stage_mismatch` means the deployment and the
   * database disagree about which game this is, so the process refuses
   * everything until a human makes them agree.
   *
   * Three words, and no numbers behind them: not which stage the database
   * holds, not which one this process was configured with, not who is in
   * maintenance or since when. `/health` is the one route that answers before
   * anybody signs in, and an unauthenticated caller learns only that the game
   * is not being served.
   */
  release: z.enum(['serving', 'maintenance', 'stage_mismatch']).optional(),
});

export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const HEALTH_ROUTE = '/health' as const;

/**
 * **Prontidão: a base responde, e é tudo o que isto diz.**
 *
 * Separado de `/health` porque as duas perguntas são diferentes e a diferença
 * já custou caro. `/health` responde "o processo está vivo" e sobrevive de
 * propósito a uma base inalcançável - o portão de lançamento cai na declaração
 * quando não consegue ler, para não mostrar um aviso de transição durante uma
 * avaria. Isso faz dele um péssimo detector de ligação: responde `ok` com a
 * base em baixo.
 *
 * Esta rota faz UMA leitura que não precisa de tabela nenhuma (`select 1`) pelo
 * MESMO caminho que o jogo usa - o mesmo `resolveConnectionString`, o mesmo
 * adaptador, o mesmo Hyperdrive quando ele está ligado. Serve de prontidão para
 * um balanceador e de primeira prova de uma ligação nova.
 *
 * **A razão de uma falha é um código fechado.** Nunca uma mensagem do driver,
 * nunca um anfitrião, nunca uma porta, nunca uma `connection string`: quem
 * chama aprende em que etapa parou, e não com que credencial.
 */
export const readinessResponseSchema = z.object({
  ready: z.boolean(),
  /** Onde a ligação foi buscada: o binding, ou a variável de ambiente. */
  via: z.enum(['hyperdrive', 'connection_string']),
  /**
   * Em que etapa falhou, quando falhou:
   *
   * - `unreachable` - não se chegou ao anfitrião (DNS, rota, IPv6, porta);
   * - `tls` - chegou-se e a cifra não se estabeleceu;
   * - `auth` - a base recusou as credenciais;
   * - `query` - ligou-se, autenticou-se, e a consulta não correu;
   * - `unknown` - nada do acima se reconheceu.
   */
  failure: z.enum(['unreachable', 'tls', 'auth', 'query', 'unknown']).optional(),
});

export type ReadinessResponse = z.infer<typeof readinessResponseSchema>;

export const READINESS_ROUTE = '/ready' as const;
